import cx from 'classnames';

import { Icon } from '@sb/design-system';

import { ConfirmationCard } from './ConfirmationCard';

interface EquipmentCardProps {
  /**
   * Name of the currently-active gripper. `null` (or empty) means no
   * gripper is configured; the card switches to the
   * "Continue without a gripper" variant.
   */
  gripperName: string | null;
  /**
   * Fires when the user taps the action button. The argument is the
   * full label string — the chat mount injects it into the input and
   * auto-sends so the user's pick lands in the conversation as if
   * they'd typed it.
   *
   * Note: this is NOT the `/confirm` HTTP flow. Tool-use approval
   * is handled separately by the ConfirmationModal flow.
   */
  onConfirm: (label: string) => void;
  autoSendDelayMs?: number;
  className?: string;
}

function makeLabel(gripperName: string | null) {
  if (gripperName && gripperName.trim().length > 0) {
    return `Confirm ${gripperName.trim()}`;
  }

  return 'Continue without a gripper';
}

/**
 * Renders the gripper-confirmation card. With an active gripper,
 * the card shows the gripper name and a primary "Confirm" button.
 * With no gripper, the card collapses to a single "Continue without
 * a gripper" primary button.
 */
export function EquipmentCard({
  gripperName,
  onConfirm,
  autoSendDelayMs,
  className,
}: EquipmentCardProps) {
  const hasGripper = !!gripperName && gripperName.trim().length > 0;
  const label = makeLabel(gripperName);

  return (
    <div className={cx('tw-flex', 'tw-flex-col', 'tw-gap-8', className)}>
      {hasGripper && (
        <div
          className={cx(
            'tw-flex',
            'tw-items-center',
            'tw-gap-10',
            'tw-px-14',
            'tw-py-12',
            'tw-rounded-[14px]',
            'tw-surface-elevated',
          )}
        >
          <Icon
            kind="equipmentBase"
            className={cx(
              'tw-shrink-0',
              'tw-icon-22',
              'tw-text-label-tertiary',
            )}
          />
          <div
            className={cx('tw-flex', 'tw-flex-col', 'tw-gap-1', 'tw-flex-1')}
          >
            <span
              className={cx(
                'tw-text-14',
                'tw-font-medium',
                'tw-text-label-primary',
              )}
            >
              {gripperName?.trim()}
            </span>
            <span className={cx('tw-text-12', 'tw-text-label-secondary')}>
              Connected and enabled
            </span>
          </div>
          {/* Green status dot. Uses the themeable `--green` token so it
              stays correct in light and dark mode. */}
          <span
            aria-hidden="true"
            className={cx(
              'tw-shrink-0',
              'tw-w-8',
              'tw-h-8',
              'tw-rounded-full',
              'tw-bg-green',
            )}
          />
        </div>
      )}
      <ConfirmationCard
        kind="confirmation"
        label={label}
        onConfirm={onConfirm}
        autoSendDelayMs={autoSendDelayMs}
      />
    </div>
  );
}
